import React, { useState, useEffect } from 'react';
import { 
  Package, Plus, Edit, Trash2, GripVertical, Check, X, 
  Shield, Building2, FileText, Landmark, Scale, Star, Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { pricingApi } from '../../lib/api';
import { toast } from 'sonner';

const ICON_OPTIONS = [
  { value: 'Shield', label: 'Shield', icon: Shield },
  { value: 'Building2', label: 'Building', icon: Building2 },
  { value: 'FileText', label: 'Document', icon: FileText },
  { value: 'Landmark', label: 'Landmark', icon: Landmark },
  { value: 'Scale', label: 'Scale', icon: Scale },
  { value: 'Star', label: 'Star', icon: Star },
];

const PricingPackagesManager = () => {
  const [loading, setLoading] = useState(true);
  const [packages, setPackages] = useState([]);
  const [editingPackage, setEditingPackage] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [form, setForm] = useState({
    name: '', name_es: '', slug: '', price: '', price_es: '',
    price_note: '', price_note_es: '', description: '', description_es: '',
    features: [], features_es: [], icon: 'Shield', popular: false, checkout_url: ''
  });
  const [newFeature, setNewFeature] = useState('');
  const [newFeatureEs, setNewFeatureEs] = useState('');

  useEffect(() => {
    loadPackages();
  }, []);

  const loadPackages = async () => {
    try {
      setLoading(true);
      const res = await pricingApi.getPackagesAdmin();
      setPackages(res.data);
    } catch (err) {
      toast.error('Failed to load pricing packages');
    } finally {
      setLoading(false);
    }
  };

  const handleSeed = async () => {
    try {
      const res = await pricingApi.seedPackages();
      toast.success(res.data.message);
      loadPackages();
    } catch (err) {
      toast.error('Failed to seed packages');
    }
  };

  const handleEdit = (pkg) => {
    setEditingPackage(pkg);
    setForm({
      name: pkg.name || '',
      name_es: pkg.name_es || '',
      slug: pkg.slug || '',
      price: pkg.price || '',
      price_es: pkg.price_es || '',
      price_note: pkg.price_note || '',
      price_note_es: pkg.price_note_es || '',
      description: pkg.description || '',
      description_es: pkg.description_es || '',
      features: pkg.features || [],
      features_es: pkg.features_es || [],
      icon: pkg.icon || 'Shield',
      popular: pkg.popular || false,
      checkout_url: pkg.checkout_url || ''
    });
    setShowEditor(true);
  };

  const handleNew = () => {
    setEditingPackage(null);
    setForm({
      name: '', name_es: '', slug: '', price: '', price_es: '',
      price_note: '', price_note_es: '', description: '', description_es: '',
      features: [], features_es: [], icon: 'Shield', popular: false, checkout_url: ''
    });
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.slug || !form.price || !form.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editingPackage) {
        await pricingApi.updatePackage(editingPackage.id, form);
        toast.success('Package updated');
      } else {
        await pricingApi.createPackage(form);
        toast.success('Package created');
      }
      setShowEditor(false);
      loadPackages();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save package');
    }
  };

  const handleDelete = async (pkg) => {
    if (!window.confirm(`Delete "${pkg.name}"?`)) return;
    try {
      await pricingApi.deletePackage(pkg.id);
      toast.success('Package deleted');
      loadPackages();
    } catch (err) {
      toast.error('Failed to delete package');
    }
  };

  const handleToggleActive = async (pkg) => {
    try {
      await pricingApi.updatePackage(pkg.id, { is_active: !pkg.is_active });
      toast.success(`Package ${pkg.is_active ? 'deactivated' : 'activated'}`);
      loadPackages();
    } catch (err) {
      toast.error('Failed to update package');
    }
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setForm(prev => ({ ...prev, features: [...prev.features, newFeature.trim()] }));
      setNewFeature('');
    }
  };

  const addFeatureEs = () => {
    if (newFeatureEs.trim()) {
      setForm(prev => ({ ...prev, features_es: [...prev.features_es, newFeatureEs.trim()] }));
      setNewFeatureEs('');
    }
  };

  const removeFeature = (index) => {
    setForm(prev => ({ ...prev, features: prev.features.filter((_, i) => i !== index) }));
  };

  const removeFeatureEs = (index) => {
    setForm(prev => ({ ...prev, features_es: prev.features_es.filter((_, i) => i !== index) }));
  };

  const IconComponent = ({ iconName }) => {
    const iconOption = ICON_OPTIONS.find(opt => opt.value === iconName);
    const Icon = iconOption?.icon || Shield;
    return <Icon className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#1F2A44]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pricing-packages-manager">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-serif text-[#222222]">Pricing Packages</h2>
          <p className="text-sm text-[#555555]">Manage your service pricing packages</p>
        </div>
        <div className="flex gap-2">
          {packages.length === 0 && (
            <Button variant="outline" onClick={handleSeed}>
              <Package className="w-4 h-4 mr-2" />
              Seed Default Packages
            </Button>
          )}
          <Button onClick={handleNew} className="bg-[#1F2A44]">
            <Plus className="w-4 h-4 mr-2" />
            Add Package
          </Button>
        </div>
      </div>

      {/* Package List */}
      {!showEditor && (
        <div className="space-y-3">
          {packages.length === 0 ? (
            <div className="bg-white border border-[#E8E4DC] rounded p-12 text-center">
              <Package className="w-12 h-12 text-[#C2A46D] mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Pricing Packages</h3>
              <p className="text-[#555555] mb-4">Create packages or seed defaults to get started.</p>
            </div>
          ) : (
            packages.map((pkg) => (
              <div key={pkg.id} className={`bg-white border rounded p-4 flex items-center justify-between ${pkg.is_active ? 'border-[#E8E4DC]' : 'border-red-200 bg-red-50/50'}`}>
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded flex items-center justify-center ${pkg.popular ? 'bg-[#1F2A44] text-white' : 'bg-[#F5F3EF] text-[#1F2A44]'}`}>
                    <IconComponent iconName={pkg.icon} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-[#222222]">{pkg.name}</h3>
                      {pkg.popular && <Badge className="bg-[#C2A46D] text-white">Popular</Badge>}
                      {!pkg.is_active && <Badge variant="outline" className="text-red-600 border-red-300">Inactive</Badge>}
                    </div>
                    <p className="text-sm text-[#555555]">{pkg.price} {pkg.price_note && <span className="text-xs">({pkg.price_note})</span>}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={pkg.is_active} 
                    onCheckedChange={() => handleToggleActive(pkg)}
                  />
                  <Button variant="outline" size="sm" onClick={() => handleEdit(pkg)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDelete(pkg)} className="text-red-600 hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Editor */}
      {showEditor && (
        <div className="bg-white border border-[#E8E4DC] rounded p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">{editingPackage ? 'Edit Package' : 'New Package'}</h3>
            <Button variant="outline" onClick={() => setShowEditor(false)}>
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Name (English) *</Label>
              <Input value={form.name} onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label>Name (Spanish)</Label>
              <Input value={form.name_es} onChange={(e) => setForm(prev => ({ ...prev, name_es: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label>Slug *</Label>
              <Input value={form.slug} onChange={(e) => setForm(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') }))} className="mt-1" placeholder="e.g., trademark-filing" disabled={!!editingPackage} />
            </div>
            <div>
              <Label>Icon</Label>
              <Select value={form.icon} onValueChange={(value) => setForm(prev => ({ ...prev, icon: value }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ICON_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Price (English) *</Label>
              <Input value={form.price} onChange={(e) => setForm(prev => ({ ...prev, price: e.target.value }))} className="mt-1" placeholder="e.g., $850" />
            </div>
            <div>
              <Label>Price (Spanish)</Label>
              <Input value={form.price_es} onChange={(e) => setForm(prev => ({ ...prev, price_es: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label>Price Note (English)</Label>
              <Input value={form.price_note} onChange={(e) => setForm(prev => ({ ...prev, price_note: e.target.value }))} className="mt-1" placeholder="e.g., + USPTO fees" />
            </div>
            <div>
              <Label>Price Note (Spanish)</Label>
              <Input value={form.price_note_es} onChange={(e) => setForm(prev => ({ ...prev, price_note_es: e.target.value }))} className="mt-1" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Description (English) *</Label>
              <Textarea value={form.description} onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))} className="mt-1" rows={3} />
            </div>
            <div>
              <Label>Description (Spanish)</Label>
              <Textarea value={form.description_es} onChange={(e) => setForm(prev => ({ ...prev, description_es: e.target.value }))} className="mt-1" rows={3} />
            </div>
          </div>

          {/* Features */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Features (English)</Label>
              <div className="space-y-2 mt-2">
                {form.features.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 bg-[#F5F3EF] p-2 rounded">
                    <span className="flex-1 text-sm">{f}</span>
                    <Button variant="ghost" size="sm" onClick={() => removeFeature(i)}><X className="w-3 h-3" /></Button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Input value={newFeature} onChange={(e) => setNewFeature(e.target.value)} placeholder="Add feature..." onKeyPress={(e) => e.key === 'Enter' && addFeature()} />
                  <Button variant="outline" size="sm" onClick={addFeature}><Plus className="w-4 h-4" /></Button>
                </div>
              </div>
            </div>
            <div>
              <Label>Features (Spanish)</Label>
              <div className="space-y-2 mt-2">
                {form.features_es.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 bg-[#F5F3EF] p-2 rounded">
                    <span className="flex-1 text-sm">{f}</span>
                    <Button variant="ghost" size="sm" onClick={() => removeFeatureEs(i)}><X className="w-3 h-3" /></Button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Input value={newFeatureEs} onChange={(e) => setNewFeatureEs(e.target.value)} placeholder="Agregar característica..." onKeyPress={(e) => e.key === 'Enter' && addFeatureEs()} />
                  <Button variant="outline" size="sm" onClick={addFeatureEs}><Plus className="w-4 h-4" /></Button>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Checkout URL (Optional)</Label>
              <Input value={form.checkout_url} onChange={(e) => setForm(prev => ({ ...prev, checkout_url: e.target.value }))} className="mt-1" placeholder="https://..." />
            </div>
            <div className="flex items-center gap-4 pt-6">
              <Switch checked={form.popular} onCheckedChange={(checked) => setForm(prev => ({ ...prev, popular: checked }))} />
              <Label>Mark as Popular</Label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowEditor(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-[#1F2A44]">
              <Check className="w-4 h-4 mr-2" />
              {editingPackage ? 'Update Package' : 'Create Package'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingPackagesManager;
